import { Injectable } from '@angular/core';
import { OneSignal } from 'onesignal-ngx';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  readonly initializedSubject = new BehaviorSubject<boolean>(false);
  readonly initialized$ = this.initializedSubject.asObservable();

  constructor(private oneSignal: OneSignal) {}

  get initialized(): boolean {
    return this.initializedSubject.getValue();
  }

  get isGranted(): boolean {
    return this.oneSignal.Notifications.permission;
  }

  async init(): Promise<void> {
    try {
      await this.oneSignal.init({
        appId: environment.oneSignal.appId,
        safari_web_id: environment.oneSignal.safari_web_id,
        // Make the notification disappear after 5s (default behavior of most browsers)
        // It seems that OneSignal make this option "true" by default!
        persistNotification: false,
        serviceWorkerParam: { scope: '/notification/' },
        serviceWorkerPath: 'notification/OneSignalSDKWorker.js',
        allowLocalhostAsSecureOrigin: environment.localLogin,
        welcomeNotification: {
          message: 'You are successfully subscribed to Ideta Inbox notifications!'
        }
      });
      this.initializedSubject.next(true);
      console.log('✅ OneSignal initialization successful');
    } catch (error) {
      this.initializedSubject.next(false);
      console.error('❌ OneSignal initialization failed with error:', error);
    }
  }

  /**
   * Login user to OneSignal. We use tag "external_id" to identify user in OneSignal so that on the backend,
   * we can send notification to that user.
   */
  async loginUser(userId: string): Promise<boolean> {
    if (!userId) {
      console.warn('⚠️ User ID is undefined, cannot login to OneSignal');
      return false;
    }

    try {
      await this.login(userId);
      await this.optIn();
      this.oneSignal.User.addTag('external_id', userId);
      return true;
    } catch (error) {
      console.error('❌ Failed to login user to OneSignal:', error);
      return false;
    }
  }

  async requestPermission(): Promise<boolean> {
    return this.oneSignal.Notifications.requestPermission();
  }

  async addTag(key: string, value: string): Promise<void> {
    return this.oneSignal.User.addTag(key, value);
  }

  async removeTag(key: string): Promise<void> {
    return this.oneSignal.User.removeTag(key);
  }

  /**
   * Opt out from receiving notifications. Please note that, the user is till connected to OneSignal. They are just
   * disabled from receiving notifications. Don't use `logout()` because `logout()` will log user out from OneSignal but they
   * still receive notifications as anonymous user.
   */
  async optOut(): Promise<void> {
    return this.oneSignal.User.PushSubscription.optOut();
  }

  async optIn(): Promise<void> {
    return this.oneSignal.User.PushSubscription.optIn();
  }

  async login(userId: string): Promise<void> {
    return this.oneSignal.login(userId);
  }

  async logout(): Promise<void> {
    return this.oneSignal.logout();
  }
}
